import { useState } from "react";

import { ComplaintQueuePanel } from "@/components/trust_suite/ComplaintQueuePanel";
import { ResidentDirectoryPanel } from "@/components/trust_suite/ResidentDirectoryPanel";
import {
  COMMUNITY_SUITE_VIEWS,
  type CommunitySuiteView,
} from "@/components/trust_suite/communitySuiteTabs";
import type { TrustComplaintQueue } from "@/api/trust_complaints";
import type { TrustTrackQueueFilter } from "@/hooks/useTrustTrack";
import { MunicipalitySwitcher } from "@/components/launch_pad/MunicipalitySwitcher";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import { Button } from "@/components/ui/button";
import { useMunicipality } from "@/context/MunicipalityContext";
import { cn } from "@/lib/utils";

// Community Suite is the employee-facing name. The section id, Flipper flag,
// routes and API keep their trust_suite identifiers; only copy changed.
type ConcernFocus = { id: number; queue: TrustTrackQueueFilter };

export function CommunitySuiteWorkspace() {
  const [view, setView] = useState<CommunitySuiteView>("concerns");
  const [focusComplaint, setFocusComplaint] = useState<ConcernFocus | null>(null);
  const [focusResidentId, setFocusResidentId] = useState<number | null>(null);
  // The concern last selected in the queue. Each view unmounts when hidden, so
  // coming back through the view strip must re-select it.
  const [lastConcern, setLastConcern] = useState<ConcernFocus | null>(null);

  // Concern and resident ids belong to one municipality. Drop them on a switch
  // so the other city's view never re-focuses a record from the previous one.
  const municipalityId = useMunicipality().municipality?.id;
  const [focusMunicipalityId, setFocusMunicipalityId] = useState(municipalityId);
  if (focusMunicipalityId !== municipalityId) {
    setFocusMunicipalityId(municipalityId);
    setLastConcern(null);
    setFocusComplaint(null);
    setFocusResidentId(null);
  }

  const handleOpenComplaint = (complaintId: number, queue: TrustComplaintQueue) => {
    setFocusComplaint({ id: complaintId, queue });
    setView("concerns");
  };

  const handleViewResident = (residentId: number) => {
    setFocusResidentId(residentId);
    setView("residents");
  };

  const selectView = (next: CommunitySuiteView) => {
    if (next === "concerns" && view !== "concerns" && lastConcern) {
      setFocusComplaint(lastConcern);
    }
    setView(next);
  };

  return (
    <div
      className={cn(moduleWorkspaceShellClass, "flex-1 min-h-0")}
      data-testid="community-suite-workspace"
    >
      <ModuleWorkspaceHeader
        title="Community Suite"
        hideTitle
        actions={<MunicipalitySwitcher />}
        meta={
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
            <h1 className="text-lg font-bold sm:text-xl">Community Suite</h1>
            <div
              role="group"
              aria-label="Community Suite views"
              className="inline-flex flex-wrap items-center gap-1 rounded-lg border bg-muted p-1"
            >
              {COMMUNITY_SUITE_VIEWS.map((item) => {
                const Icon = item.icon;
                const selected = view === item.id;
                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    size="sm"
                    aria-pressed={selected}
                    className={cn(
                      "h-7 px-3 text-muted-foreground hover:bg-background/60 hover:text-foreground",
                      selected && "bg-background text-foreground shadow-sm hover:bg-background",
                    )}
                    onClick={() => selectView(item.id)}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {item.label}
                  </Button>
                );
              })}
            </div>
          </div>
        }
        density="compact"
      />

      <ModuleWorkspaceBody
        className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", "!p-0")}
        contentClassName="flex h-full min-h-0 flex-1 flex-col"
      >
        {view === "concerns" ? (
          <ComplaintQueuePanel
            focusComplaint={focusComplaint}
            onFocusComplaintConsumed={() => setFocusComplaint(null)}
            onViewResident={handleViewResident}
            onSelectConcern={setLastConcern}
          />
        ) : null}
        {view === "residents" ? (
          <ResidentDirectoryPanel
            onOpenComplaint={handleOpenComplaint}
            focusResidentId={focusResidentId}
            onFocusResidentConsumed={() => setFocusResidentId(null)}
          />
        ) : null}
      </ModuleWorkspaceBody>
    </div>
  );
}
